import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Warehouse } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);

// Admin › Dealers cell: current GS / BP bays (or "Set bays" when a 2S / 3S workshop has none yet; "—" for 1S / Studio / Festive).
export function BayCell({ dealer, onOpen }) {
  const b = dealer.bays || {};
  if (!["2S", "3S"].includes(dealer.facility)) return <span className="text-xs text-muted-foreground" data-testid={`dealer-bays-${dealer.code}`}>— {dealer.facility}</span>;
  return (
    <button onClick={onOpen} data-testid={`dealer-bays-${dealer.code}`}
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors duration-200 hover:bg-secondary ${b.needs_bays ? "border-amber-400 bg-amber-50 text-amber-900" : ""}`}>
      <Warehouse size={12} />
      {b.bays_set ? <><span className="num font-semibold">GS {b.gs_bays}</span><span className="text-muted-foreground">·</span><span className="num font-semibold">BP {b.bp_bays}</span></> : "Set bays"}
    </button>
  );
}

export function BayDialog({ dealer, onClose, onSaved }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ gs_bays: "", bp_bays: "", effective_from: today(), note: "" });
  const [saving, setSaving] = useState(false);
  const code = dealer?.code;

  useEffect(() => {
    if (!code) return;
    setInfo(null);
    api.get(`/admin/dealers/${encodeURIComponent(code)}/bays`).then(({ data }) => {
      setInfo(data);
      setForm({ gs_bays: data.current?.gs_bays ?? "", bp_bays: data.current?.bp_bays ?? "", effective_from: today(), note: "" });
    }).catch((e) => toast.error(apiError(e)));
  }, [code]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/dealers/${encodeURIComponent(code)}/bays`, { gs_bays: Number(form.gs_bays || 0), bp_bays: Number(form.bp_bays || 0), effective_from: form.effective_from, note: form.note });
      setInfo((i) => ({ ...i, current: data.current, history: data.history }));
      setForm((f) => ({ ...f, note: "" }));
      toast.success(`${code} bays saved from ${form.effective_from}`);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const remove = async (eff) => {
    if (!window.confirm(`Remove the bay record effective ${eff}?`)) return;
    try {
      const { data } = await api.delete(`/admin/dealers/${encodeURIComponent(code)}/bays/${eff}`);
      const { data: fresh } = await api.get(`/admin/dealers/${encodeURIComponent(code)}/bays`);
      setInfo({ ...fresh, history: data.history });
      toast.success("Bay record removed");
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const cur = info?.current;
  return (
    <Dialog open={!!dealer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bay-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Bays — {code} {dealer?.name} <span className="text-xs font-normal text-muted-foreground">({dealer?.facility})</span></DialogTitle>
          <DialogDescription className="text-xs">GS bays serve Free / Paid Service and Running Repair; BP bays serve Body &amp; Paint. Changes are effective-dated — productivity for earlier days keeps the bays that were in force then.</DialogDescription>
        </DialogHeader>
        {cur && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs flex flex-wrap gap-x-4 gap-y-1" data-testid="bay-current">
            <span>Today: <b className="num" data-testid="bay-current-gs">GS {cur.gs_bays ?? "—"}</b> · <b className="num" data-testid="bay-current-bp">BP {cur.bp_bays ?? "—"}</b></span>
            {cur.effective_from && <span className="text-muted-foreground">in force since {cur.effective_from}</span>}
            {cur.needs_bays && <span className="text-amber-700 font-semibold">No bays captured yet</span>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">GS bays</label>
            <Input type="number" min={0} max={500} value={form.gs_bays} onChange={(e) => setForm({ ...form, gs_bays: e.target.value })} className="h-9 text-sm" data-testid="bay-gs-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">BP bays</label>
            <Input type="number" min={0} max={500} value={form.bp_bays} onChange={(e) => setForm({ ...form, bp_bays: e.target.value })} className="h-9 text-sm" data-testid="bay-bp-input" />
          </div>
          <div className="col-span-2">
            <label className="text-xs font-semibold text-muted-foreground block mb-1">Effective from</label>
            <Input type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} className="h-9 text-sm" data-testid="bay-effective-input" />
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground block mb-1">Note (optional)</label>
          <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. 2 new GS bays commissioned" className="h-9 text-sm" data-testid="bay-note-input" />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Saving on an existing date replaces that record.</span>
          <Button onClick={save} disabled={saving || !form.effective_from || (Number(form.gs_bays || 0) + Number(form.bp_bays || 0)) === 0} data-testid="bay-save-btn">Save bays</Button>
        </div>
        <div className="border rounded-md overflow-auto max-h-56" data-testid="bay-history">
          <table className="w-full text-xs">
            <thead className="border-b bg-muted/40">
              <tr>{["Effective from", "GS", "BP", "Note", "By", ""].map((h) => <th key={h} className="px-2 py-1.5 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{h}</th>)}</tr>
            </thead>
            <tbody>
              {(info?.history || []).map((r) => (
                <tr key={r.effective_from} className="border-b last:border-0" data-testid={`bay-history-${r.effective_from}`}>
                  <td className="px-2 py-1.5 num font-medium">{r.effective_from}</td>
                  <td className="px-2 py-1.5 num">{r.gs_bays}</td>
                  <td className="px-2 py-1.5 num">{r.bp_bays}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{r.note || "—"}</td>
                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[140px]" title={r.by}>{r.by}</td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => remove(r.effective_from)} className="text-muted-foreground hover:text-red-600 transition-colors duration-200" data-testid={`bay-delete-${r.effective_from}`}><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
              {info && !info.history?.length && <tr><td colSpan={6} className="px-2 py-3 text-center text-muted-foreground" data-testid="bay-history-empty">No bay records yet</td></tr>}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
